import {
  AgreementFactory,
  AgreementKindEnum,
  AgreementStateEnum,
  type Agreement,
  type AgreementDTO,
  type EaAgreement,
  type EaAgreementDTO,
  type EdAgreement,
  type EdAgreementDTO,
  type IpAgreement,
  type IpAgreementDTO,
  type PaAgreement,
  type PaAgreementDTO,
  type RfAgreement,
  type RfAgreementDTO,
} from './agreement';

// AgreementsDTO is the wire shape — the agreements payload as the BFF
// serializes it. React components consume the enriched collection built from it.
export type AgreementsDTO = {
  paAgreements?: PaAgreementDTO[];
  edAgreement?: EdAgreementDTO;
  eaAgreement?: EaAgreementDTO;
  ipAgreements?: IpAgreementDTO[];
  rfAgreement?: RfAgreementDTO;
  numberOfAgreements?: number;
};

/** The `AgreementsDTO` in the envelope the agreements endpoint returns. */
export type AgreementsResponse = {
  agreements?: AgreementsDTO;
};

// Wire shape of the currently-selected agreement per kind (derived from the
// search selection, not sent by the BFF).
export type EnabledAgreementsDTO = {
  paAgreement?: PaAgreementDTO;
  eaAgreement?: EaAgreementDTO;
  edAgreement?: EdAgreementDTO;
  rfAgreement?: RfAgreementDTO;
};

// The currently-selected agreement per kind, enriched.
export type EnabledAgreements = {
  paAgreement?: PaAgreement;
  eaAgreement?: EaAgreement;
  edAgreement?: EdAgreement;
  rfAgreement?: RfAgreement;
};

// Agreements is the enriched, React-facing collection: enriched members plus
// the intrinsic premium-access aggregates. Cross-domain selection logic (which
// agreement a given search enables) lives in the agreement domain service, so
// this collection stays independent of the search-parameter domain.
export interface Agreements {
  readonly paAgreements: PaAgreement[];
  readonly eaAgreement?: EaAgreement;
  readonly edAgreement?: EdAgreement;
  readonly ipAgreements: IpAgreement[];
  readonly rfAgreement?: RfAgreement;
  /**
   * Every agreement the visitor holds, keyed by the value that identifies it
   * in a search: `${kind}:${id}`.
   *
   * The typed slots above say what each kind *is*; this says what the visitor
   * *has*. Questions of the second sort -- does the visitor own this, which
   * agreement does this selection point at -- are not per-kind, but without a
   * uniform view every caller had to hand-roll the same five-way fan-out to
   * ask them. The wire format is already uniform, so the model can be too.
   *
   * Includes IP agreements: they are owned like any other, even though no
   * search parameter selects one.
   */
  readonly byFilterValue: ReadonlyMap<string, Agreement>;
  readonly numberOfAgreements: number;
  areAllPaViewOnly(): boolean;
  areAllPaExhausted(): boolean;
  /** Whether the visitor holds the agreement a filter value names. */
  owns(filterValue: string): boolean;
  /** The agreement a filter value names, when it is of the expected kind. */
  ofKind<K extends AgreementKindEnum>(
    filterValue: string | undefined,
    kind: K
  ): Extract<Agreement, { kind: K }> | undefined;
  /** Premium-access agreements with allowance left. */
  availablePaAgreements(): PaAgreement[];
}

// Narrows AgreementFactory output to a single kind without a cast — types the
// per-kind members of the enriched collection.
const isKind =
  <K extends AgreementKindEnum>(kind: K) =>
  (agreement: Agreement): agreement is Extract<Agreement, { kind: K }> =>
    agreement.kind === kind;

export class AgreementsFactory {
  #agreementFactory = new AgreementFactory();

  #enrichList<K extends AgreementKindEnum>(
    list: AgreementDTO[] | undefined,
    kind: K,
    agencyUnlimited: boolean
  ): Extract<Agreement, { kind: K }>[] {
    return (list ?? [])
      .map((member) => this.#agreementFactory.fromDTO(member, agencyUnlimited))
      .filter(isKind(kind));
  }

  #enrichOne<K extends AgreementKindEnum>(
    member: AgreementDTO | undefined,
    kind: K,
    agencyUnlimited: boolean
  ): Extract<Agreement, { kind: K }> | undefined {
    if (!member) return undefined;
    const enriched = this.#agreementFactory.fromDTO(member, agencyUnlimited);
    return isKind(kind)(enriched) ? enriched : undefined;
  }

  fromDTO = (dto: AgreementsDTO, agencyUnlimited = false): Agreements => {
    const paAgreements = this.#enrichList(
      dto.paAgreements,
      AgreementKindEnum.PA,
      agencyUnlimited
    );
    const ipAgreements = this.#enrichList(
      dto.ipAgreements,
      AgreementKindEnum.IP,
      agencyUnlimited
    );
    const eaAgreement = this.#enrichOne(
      dto.eaAgreement,
      AgreementKindEnum.EA,
      agencyUnlimited
    );
    const edAgreement = this.#enrichOne(
      dto.edAgreement,
      AgreementKindEnum.ED,
      agencyUnlimited
    );
    const rfAgreement = this.#enrichOne(
      dto.rfAgreement,
      AgreementKindEnum.RF,
      agencyUnlimited
    );

    const byFilterValue = new Map(
      [
        ...paAgreements,
        ...ipAgreements,
        ...(eaAgreement ? [eaAgreement] : []),
        ...(edAgreement ? [edAgreement] : []),
        ...(rfAgreement ? [rfAgreement] : []),
      ].map((one) => [one.filterValue, one])
    );

    return {
      paAgreements,
      eaAgreement,
      edAgreement,
      ipAgreements,
      rfAgreement,
      byFilterValue,
      numberOfAgreements: dto.numberOfAgreements ?? 0,
      // No PA agreements is not "all view-only / all exhausted" (absent PA
      // data → false), hence the length guard.
      areAllPaViewOnly: () =>
        paAgreements.length > 0 &&
        paAgreements.every((pa) => pa.state === AgreementStateEnum.ViewOnly),
      areAllPaExhausted: () =>
        paAgreements.length > 0 && paAgreements.every((pa) => pa.isExhausted()),
      owns: (filterValue) => byFilterValue.has(filterValue),
      // The map is keyed across every kind, so the kind check is not a filter:
      // it is what makes the narrowing sound.
      ofKind: (filterValue, kind) => {
        const agreement = filterValue
          ? byFilterValue.get(filterValue)
          : undefined;

        return agreement?.kind === kind
          ? (agreement as Extract<Agreement, { kind: typeof kind }>)
          : undefined;
      },
      availablePaAgreements: () =>
        paAgreements.filter((pa) => !pa.isExhausted()),
    };
  };
}
